import { useState, useEffect, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, subYears, addMonths, format } from 'date-fns';
import { sk } from 'date-fns/locale';
import 'react-day-picker/dist/style.css';

export interface FilterState {
  // Date filters
  datePreset: 'week' | 'month' | 'lastMonth' | 'twoYearsAgo' | 'custom' | null;
  dateFrom: Date | null;
  dateTo: Date | null;

  // Amount filters
  amountPreset: '<20' | '20-50' | '50-200' | '>200' | 'custom' | null;
  amountMin: string;
  amountMax: string;

  // Split status
  splitStatus: 'all' | 'split' | 'nonsplit';

  // Merchant filter
  selectedMerchants: string[];
  merchantSearch: string;

  // Category filter (single select, any level)
  selectedCategory: string | null;

  // Item search
  itemSearch: string;
}

interface SearchFilterPanelProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onApply: () => void;
  onClear: () => void;
  availableMerchants: string[];
  isLoading?: boolean;
}

interface TaxonomyCategory {
  id: string;
  name: string;
  level: number;
  children?: TaxonomyCategory[];
}

export default function SearchFilterPanel({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  availableMerchants,
  isLoading = false
}: SearchFilterPanelProps) {
  const [expandedSections, setExpandedSections] = useState({
    date: true,
    merchant: false,
    amount: false,
    split: false,
    advanced: false
  });

  const [showCalendar, setShowCalendar] = useState(false);
  const [taxonomy, setTaxonomy] = useState<TaxonomyCategory[]>([]);

  // Category selection state (cascading dropdowns)
  const [level1Selected, setLevel1Selected] = useState<string>('');
  const [level2Selected, setLevel2Selected] = useState<string>('');
  const [level3Selected, setLevel3Selected] = useState<string>('');
  const [level4Selected, setLevel4Selected] = useState<string>('');

  // Load taxonomy for category filter
  useEffect(() => {
    fetch('/taxonomy.sk.json')
      .then(res => res.json())
      .then(data => setTaxonomy(data.taxonomy || []))
      .catch(err => console.error('Failed to load taxonomy:', err));
  }, []);

  // Get available options for each category level
  const getLevel2Options = (): TaxonomyCategory[] => {
    if (!level1Selected) return [];
    const level1Node = taxonomy.find(n => n.id === level1Selected);
    return level1Node?.children || [];
  };

  const getLevel3Options = (): TaxonomyCategory[] => {
    if (!level2Selected) return [];
    const level1Node = taxonomy.find(n => n.id === level1Selected);
    const level2Node = level1Node?.children?.find(n => n.id === level2Selected);
    return level2Node?.children || [];
  };

  const getLevel4Options = (): TaxonomyCategory[] => {
    if (!level3Selected) return [];
    const level1Node = taxonomy.find(n => n.id === level1Selected);
    const level2Node = level1Node?.children?.find(n => n.id === level2Selected);
    const level3Node = level2Node?.children?.find(n => n.id === level3Selected);
    return level3Node?.children || [];
  };

  // Handle category level changes
  const handleLevel1Change = (value: string) => {
    setLevel1Selected(value);
    setLevel2Selected('');
    setLevel3Selected('');
    setLevel4Selected('');
    onFiltersChange({ ...filters, selectedCategory: value || null });
  };

  const handleLevel2Change = (value: string) => {
    setLevel2Selected(value);
    setLevel3Selected('');
    setLevel4Selected('');
    onFiltersChange({ ...filters, selectedCategory: value || level1Selected || null });
  };

  const handleLevel3Change = (value: string) => {
    setLevel3Selected(value);
    setLevel4Selected('');
    onFiltersChange({ ...filters, selectedCategory: value || level2Selected || null });
  };

  const handleLevel4Change = (value: string) => {
    setLevel4Selected(value);
    onFiltersChange({ ...filters, selectedCategory: value || level3Selected || null });
  };

  // Get category name for chip display
  const getCategoryName = (categoryId: string): string => {
    const findNode = (nodes: TaxonomyCategory[], id: string): TaxonomyCategory | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNode(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    return findNode(taxonomy, categoryId)?.name || categoryId;
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Date preset handlers
  const handleDatePreset = (preset: FilterState['datePreset']) => {
    const today = new Date();
    let from: Date | null = null;
    let to: Date | null = null;

    switch (preset) {
      case 'week':
        from = startOfWeek(today, { weekStartsOn: 1 }); // Monday
        to = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'month':
        from = startOfMonth(today);
        to = endOfMonth(today);
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        from = startOfMonth(lastMonth);
        to = endOfMonth(lastMonth);
        break;
      case 'twoYearsAgo':
        // Warranty check: 2 years ago ± 1 month
        const twoYearsAgo = subYears(today, 2);
        from = subMonths(twoYearsAgo, 1); // 1 month before
        to = addMonths(twoYearsAgo, 1);   // 1 month after
        break;
      case 'custom':
        setShowCalendar(true);
        break;
    }

    onFiltersChange({
      ...filters,
      datePreset: preset,
      dateFrom: from,
      dateTo: to
    });
  };

  // Amount preset handlers
  const handleAmountPreset = (preset: FilterState['amountPreset']) => {
    let min = '';
    let max = '';

    switch (preset) {
      case '<20':
        min = '';
        max = '20';
        break;
      case '20-50':
        min = '20';
        max = '50';
        break;
      case '50-200':
        min = '50';
        max = '200';
        break;
      case '>200':
        min = '200';
        max = '';
        break;
      case 'custom':
        // Keep existing values
        break;
    }

    onFiltersChange({
      ...filters,
      amountPreset: preset,
      amountMin: min,
      amountMax: max
    });
  };

  // Merchant selection handlers
  const filteredMerchants = useMemo(() => {
    if (!filters.merchantSearch) return availableMerchants;
    const search = filters.merchantSearch.toLowerCase();
    return availableMerchants.filter(m => m.toLowerCase().includes(search));
  }, [availableMerchants, filters.merchantSearch]);

  const toggleMerchant = (merchant: string) => {
    const selected = filters.selectedMerchants.includes(merchant);
    onFiltersChange({
      ...filters,
      selectedMerchants: selected
        ? filters.selectedMerchants.filter(m => m !== merchant)
        : [...filters.selectedMerchants, merchant]
    });
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.datePreset) count++;
    if (filters.amountPreset) count++;
    if (filters.splitStatus !== 'all') count++;
    if (filters.selectedMerchants.length > 0) count++;
    if (filters.selectedCategory) count++;
    if (filters.itemSearch) count++;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      marginBottom: '20px',
      overflow: 'hidden'
    }}>
      {/* Active filters chips */}
      {hasActiveFilters && (
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>
            Aktívne filtre ({activeFilterCount}):
          </span>

          {filters.datePreset && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#ede9fe',
              color: '#6b21a8',
              padding: '4px 10px',
              borderRadius: '16px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              📅 {filters.datePreset === 'week' ? 'Tento týždeň' :
                   filters.datePreset === 'month' ? 'Tento mesiac' :
                   filters.datePreset === 'lastMonth' ? 'Minulý mesiac' :
                   filters.datePreset === 'twoYearsAgo' ? 'Záruka' :
                   `${filters.dateFrom ? format(filters.dateFrom, 'dd.MM.yyyy') : ''} - ${filters.dateTo ? format(filters.dateTo, 'dd.MM.yyyy') : ''}`}
              <button
                onClick={() => onFiltersChange({ ...filters, datePreset: null, dateFrom: null, dateTo: null })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px' }}
              >
                ×
              </button>
            </div>
          )}

          {filters.selectedMerchants.length > 0 && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#dbeafe',
              color: '#1e40af',
              padding: '4px 10px',
              borderRadius: '16px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              🏪 {filters.selectedMerchants.length} obchodov
              <button
                onClick={() => onFiltersChange({ ...filters, selectedMerchants: [] })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px' }}
              >
                ×
              </button>
            </div>
          )}

          {filters.amountPreset && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#d1fae5',
              color: '#065f46',
              padding: '4px 10px',
              borderRadius: '16px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              💰 {filters.amountPreset === 'custom'
                ? `${filters.amountMin || '0'}-${filters.amountMax || '∞'} EUR`
                : filters.amountPreset === '<20' ? '<20 EUR'
                : filters.amountPreset === '>200' ? '>200 EUR'
                : filters.amountPreset}
              <button
                onClick={() => onFiltersChange({ ...filters, amountPreset: null, amountMin: '', amountMax: '' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px' }}
              >
                ×
              </button>
            </div>
          )}

          {filters.splitStatus !== 'all' && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#fef3c7',
              color: '#92400e',
              padding: '4px 10px',
              borderRadius: '16px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              👥 {filters.splitStatus === 'split' ? 'Rozdelené' : 'Nerozdelené'}
              <button
                onClick={() => onFiltersChange({ ...filters, splitStatus: 'all' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px' }}
              >
                ×
              </button>
            </div>
          )}

          {filters.selectedCategory && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#fee2e2',
              color: '#991b1b',
              padding: '4px 10px',
              borderRadius: '16px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              🏷️ {getCategoryName(filters.selectedCategory)}
              <button
                onClick={() => {
                  setLevel1Selected('');
                  setLevel2Selected('');
                  setLevel3Selected('');
                  setLevel4Selected('');
                  onFiltersChange({ ...filters, selectedCategory: null });
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px' }}
              >
                ×
              </button>
            </div>
          )}

          {filters.itemSearch && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#e0e7ff',
              color: '#3730a3',
              padding: '4px 10px',
              borderRadius: '16px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              🔍 "{filters.itemSearch}"
              <button
                onClick={() => onFiltersChange({ ...filters, itemSearch: '' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px' }}
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filter sections */}
      <div style={{ padding: '16px' }}>

        {/* Date Filter */}
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={() => toggleSection('date')}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '8px 0',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              color: '#111827'
            }}
          >
            <span>📅 Obdobie</span>
            <span style={{ fontSize: '20px', transform: expandedSections.date ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </button>

          {expandedSections.date && (
            <div style={{ marginTop: '12px', paddingLeft: '8px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {['week', 'month', 'lastMonth', 'twoYearsAgo', 'custom'].map(preset => (
                  <label key={preset} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="datePreset"
                      checked={filters.datePreset === preset}
                      onChange={() => handleDatePreset(preset as FilterState['datePreset'])}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151', lineHeight: '1.4' }}>
                      {preset === 'week' ? 'Tento týždeň' :
                       preset === 'month' ? 'Tento mesiac' :
                       preset === 'lastMonth' ? 'Minulý mesiac' :
                       preset === 'twoYearsAgo' ? 'Spred 2 rokov - pravdepodobne sa končí záruka' :
                       'Vlastné'}
                    </span>
                  </label>
                ))}
              </div>

              {showCalendar && filters.datePreset === 'custom' && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                  <DayPicker
                    mode="range"
                    selected={{ from: filters.dateFrom || undefined, to: filters.dateTo || undefined }}
                    onSelect={(range) => {
                      onFiltersChange({
                        ...filters,
                        dateFrom: range?.from || null,
                        dateTo: range?.to || null
                      });
                    }}
                    locale={sk}
                    footer={
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                        {filters.dateFrom && filters.dateTo
                          ? `${format(filters.dateFrom, 'dd.MM.yyyy')} - ${format(filters.dateTo, 'dd.MM.yyyy')}`
                          : 'Vyberte časové obdobie'}
                      </div>
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Merchant Filter */}
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={() => toggleSection('merchant')}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '8px 0',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              color: '#111827'
            }}
          >
            <span>🏪 Obchodník</span>
            <span style={{ fontSize: '20px', transform: expandedSections.merchant ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </button>

          {expandedSections.merchant && (
            <div style={{ marginTop: '12px', paddingLeft: '8px' }}>
              <input
                type="text"
                placeholder="Hľadať obchodník..."
                value={filters.merchantSearch}
                onChange={(e) => onFiltersChange({ ...filters, merchantSearch: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              />

              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {filteredMerchants.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af', padding: '8px 0' }}>
                    Žiadne výsledky
                  </p>
                ) : (
                  filteredMerchants.slice(0, 20).map(merchant => (
                    <label key={merchant} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={filters.selectedMerchants.includes(merchant)}
                        onChange={() => toggleMerchant(merchant)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{merchant}</span>
                    </label>
                  ))
                )}
              </div>

              {filteredMerchants.length > 20 && (
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                  Zobrazených prvých 20 z {filteredMerchants.length} výsledkov
                </p>
              )}
            </div>
          )}
        </div>

        {/* Amount Filter */}
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={() => toggleSection('amount')}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '8px 0',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              color: '#111827'
            }}
          >
            <span>💰 Suma</span>
            <span style={{ fontSize: '20px', transform: expandedSections.amount ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </button>

          {expandedSections.amount && (
            <div style={{ marginTop: '12px', paddingLeft: '8px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {['<20', '20-50', '50-200', '>200', 'custom'].map(preset => (
                  <label key={preset} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="amountPreset"
                      checked={filters.amountPreset === preset}
                      onChange={() => handleAmountPreset(preset as FilterState['amountPreset'])}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      {preset === '<20' ? '<20 EUR' :
                       preset === '20-50' ? '20-50 EUR' :
                       preset === '50-200' ? '50-200 EUR' :
                       preset === '>200' ? '>200 EUR' :
                       'Vlastné'}
                    </span>
                  </label>
                ))}
              </div>

              {filters.amountPreset === 'custom' && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.amountMin}
                    onChange={(e) => onFiltersChange({ ...filters, amountMin: e.target.value })}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{ color: '#9ca3af' }}>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.amountMax}
                    onChange={(e) => onFiltersChange({ ...filters, amountMax: e.target.value })}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>EUR</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Split Status Filter */}
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={() => toggleSection('split')}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '8px 0',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              color: '#111827'
            }}
          >
            <span>👥 Rozdelené bločky</span>
            <span style={{ fontSize: '20px', transform: expandedSections.split ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </button>

          {expandedSections.split && (
            <div style={{ marginTop: '12px', paddingLeft: '8px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { value: 'all', label: 'Všetky bločky' },
                  { value: 'split', label: 'Len rozdelené' },
                  { value: 'nonsplit', label: 'Len nerozdelené' }
                ].map(option => (
                  <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="splitStatus"
                      checked={filters.splitStatus === option.value}
                      onChange={() => onFiltersChange({ ...filters, splitStatus: option.value as FilterState['splitStatus'] })}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Advanced Filters */}
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={() => toggleSection('advanced')}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '8px 0',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              color: '#111827'
            }}
          >
            <span>⚡ Pokročilé vyhľadávanie</span>
            <span style={{ fontSize: '20px', transform: expandedSections.advanced ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </button>

          {expandedSections.advanced && (
            <div style={{ marginTop: '12px', paddingLeft: '8px' }}>
              {/* Category filter - cascading dropdowns */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', display: 'block' }}>
                  🏷️ Kategória
                </label>

                {/* Level 1 */}
                <select
                  value={level1Selected}
                  onChange={(e) => handleLevel1Change(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff',
                    marginBottom: '8px'
                  }}
                >
                  <option value="">Všetky kategórie</option>
                  {taxonomy.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                {/* Level 2 */}
                {level1Selected && getLevel2Options().length > 0 && (
                  <select
                    value={level2Selected}
                    onChange={(e) => handleLevel2Change(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#fff',
                      marginBottom: '8px'
                    }}
                  >
                    <option value="">----</option>
                    {getLevel2Options().map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Level 3 */}
                {level2Selected && getLevel3Options().length > 0 && (
                  <select
                    value={level3Selected}
                    onChange={(e) => handleLevel3Change(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#fff',
                      marginBottom: '8px'
                    }}
                  >
                    <option value="">----</option>
                    {getLevel3Options().map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Level 4 */}
                {level3Selected && getLevel4Options().length > 0 && (
                  <select
                    value={level4Selected}
                    onChange={(e) => handleLevel4Change(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#fff',
                      marginBottom: '8px'
                    }}
                  >
                    <option value="">----</option>
                    {getLevel4Options().map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Item search */}
              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', display: 'block' }}>
                  🔍 Vyhľadať položku
                </label>
                <input
                  type="text"
                  placeholder="napr. chlieb, mlieko..."
                  value={filters.itemSearch}
                  onChange={(e) => onFiltersChange({ ...filters, itemSearch: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', marginBottom: 0 }}>
                  Nájde všetky bločky obsahujúce túto položku
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        gap: '12px',
        justifyContent: 'space-between'
      }}>
        <button
          onClick={onClear}
          disabled={!hasActiveFilters || isLoading}
          style={{
            background: 'none',
            border: 'none',
            color: hasActiveFilters ? '#667eea' : '#9ca3af',
            fontSize: '14px',
            fontWeight: 600,
            cursor: hasActiveFilters && !isLoading ? 'pointer' : 'not-allowed',
            textDecoration: 'underline',
            padding: '8px 16px'
          }}
        >
          Vymazať filtre
        </button>

        <button
          onClick={onApply}
          disabled={isLoading}
          style={{
            flex: 1,
            background: isLoading ? '#9ca3af' : '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'Aplikujem...' : 'Použiť filtre'}
        </button>
      </div>
    </div>
  );
}
